import { useEffect } from 'react'
import { useBlocker } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/Button'

/**
 * Stops a page with unsaved work from being navigated away from without being asked about.
 *
 * Two exits, two mechanisms. Navigation inside the app is held by the router's own blocker, which
 * catches a link and a programmatic navigate alike, and lets the answer be a dialog of our own.
 * Leaving the site — a reload, a closed tab, a typed address — can only be met with the browser's
 * built-in prompt, which cannot be styled or worded. Both are better than losing the work.
 */
export function useUnsavedChangesPrompt(when: boolean) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    if (!when) return

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Set for the browsers that still read it; none of them show the text any more.
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [when])

  // A page that stops being dirty while the question is on screen has nothing left to ask about.
  useEffect(() => {
    if (blocker.state === 'blocked' && !when) blocker.reset?.()
  }, [blocker, when])

  return blocker
}

/**
 * The question itself: stay, leave anyway, or save and then leave.
 *
 * Leaving is the destructive answer but it is also the one being asked for, so it reads as the
 * main button rather than as a warning. Saving sits apart on the left: it is the way out that
 * costs nothing, and putting it beside the other two would make three buttons to weigh up.
 */
export function UnsavedChangesDialog({
  open,
  onStay,
  onDiscard,
  onSave,
  saving = false,
}: {
  open: boolean
  onStay: () => void
  onDiscard: () => void
  /** Left out when the page has no way to save from here. */
  onSave?: () => void
  saving?: boolean
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onStay()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
          <Dialog.Title className="text-base font-semibold text-gray-900">
            Unsaved changes
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-gray-500">
            You have unsaved changes. Are you sure you want to leave this page and discard your
            changes?
          </Dialog.Description>
          <div className="mt-6 flex items-center justify-between gap-2">
            <div>
              {onSave && (
                <Button
                  type="button"
                  variant="ghost"
                  className="px-0 text-gray-900 hover:bg-transparent hover:underline"
                  disabled={saving}
                  onClick={onSave}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onStay}>
                Go back
              </Button>
              <Button type="button" onClick={onDiscard} disabled={saving}>
                Discard changes
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
